import React from 'react'
import { AppState, User } from '../../../providers/context'
import { Popups } from '../../../providers/popups'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import Delta from 'quill-delta'
import {
  StudentRecipient,
  ComposeState,
  DEFAULT_COMPOSE_STATE,
  SendType,
  StudentGroupRecipient,
  SingleStudentRecipient,
  DEFAULT_RECIPIENTS_INFO,
  TeacherRecipient,
  StudentGuardianRecipient,
  Recipient,
} from './composeTypes'
import { deltaToText } from '../../../components/quill/quillTextarea'
import assert from 'assert'
import { MessageData } from '../message'
import { stringToDelta } from '../../../utils'

export const useUnreachableRecipientsWarning = (isTeacher: boolean, composeState: ComposeState) => {
  return React.useMemo(() => {
    if (!isTeacher) return undefined
    const selectedRecipients = composeState.selectedRecipients as StudentRecipient[]

    const sendingMass = composeState.sendTypes.has('mass')

    const names = []
    for (const r of selectedRecipients) {
      if (!r.group) {
        if (
          composeState.recipientsInfo.toStudents &&
          (sendingMass ? r.contactable.unreachableSelfMass : r.contactable.unreachableSelf)
        )
          names.push(r.name)
        if (
          composeState.recipientsInfo.toGuardians &&
          (sendingMass ? r.contactable.selfHasNoReachableParentsMass : r.contactable.selfHasNoReachableParents)
        )
          names.push('Any guardian of ' + r.name)
      } else {
        if (composeState.recipientsInfo.toStudents)
          names.push(
            ...((sendingMass ? r.contactable.unreachableStudentsMass : r.contactable.unreachableStudents) || [])
          )
        if (composeState.recipientsInfo.toGuardians)
          names.push(
            ...(
              (sendingMass
                ? r.contactable.studentsWithoutReachableParentsMass
                : r.contactable.studentsWithoutReachableParents) || []
            ).map((name) => 'Any guardian of ' + name)
          )
      }
    }

    if (names.length === 0) return undefined

    const uniqueNames = [...new Set(names)]
    const more = uniqueNames.length > 20 ? `<b>And ${uniqueNames.length - 20} more</b>` : ''

    return `<b>The following selected recipients will not receive this message:</b><br>${uniqueNames.slice(0, 20).join('<br>')}${more}`
  }, [composeState, isTeacher])
}

export type PreviewState =
  | {
      count: number | undefined
    }
  | undefined
export const usePreviewState = (isTeacher: boolean, composeState: ComposeState): PreviewState => {
  return React.useMemo(() => {
    if ((composeState.sendTypes.has('phone') && !composeState.sendTypes.has('mass')) || !isTeacher) return undefined
    const selectedRecipients = composeState.selectedRecipients as StudentRecipient[]

    let count = composeState.recipientsInfo.individuals.length

    if (
      count === 0 &&
      !composeState.recipientsInfo.toGuardians &&
      !composeState.recipientsInfo.toStudents &&
      !composeState.recipientsInfo.toSharedTeachers
    )
      // no recipients selected, no preview available
      return undefined

    const people = new Set()
    if (composeState.recipientsInfo.toStudents)
      for (const r of selectedRecipients)
        if (!r.group) people.add(r.id)
        else for (const s of r.memberIDs || []) people.add(s)

    if (composeState.recipientsInfo.toGuardians)
      for (const r of selectedRecipients)
        if (!r.group) for (const g of r.guardians) people.add(g.id)
        else for (const g of r.guardianIDs || []) people.add(g)

    count += people.size

    if (composeState.recipientsInfo.toSharedTeachers)
      for (const tr of composeState.recipientsInfo.sharedTeacherRecipients)
        if (tr.group && !tr.teacherCount) return { count: undefined }
        else count += tr.group ? tr.teacherCount! : 1

    return { count }
  }, [composeState.recipientsInfo, composeState.selectedRecipients, composeState.sendTypes, composeState.sendOption])
}

export const useSaveGroup = (
  setLoadingGroup: React.Dispatch<React.SetStateAction<boolean>>,
  composeState: ComposeState,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>,
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(StudentRecipient | TeacherRecipient)[] | undefined>>,
  setState: React.Dispatch<React.SetStateAction<AppState>>,
  user: User,
  reloadContacts: () => void
) => {
  return React.useCallback(() => {
    Popups.prompt({
      title: 'Enter a name for this group',
      defaultValue: 'New Group',
      confirm: 'Make Group',
      cancel: 'Cancel',
      message: 'Recipients will see this name when you send to this group.',
    }).then((name) => {
      if (name === undefined) return
      assert(user.type === 'teacher', 'Only teachers can save groups')
      const selectedRecipients = composeState.selectedRecipients as StudentRecipient[]
      setLoadingGroup(true)
      makeRequest<StudentGroupRecipient | 'err-grouping-custom' | 'err-grouping-schools' | 'err-grouping-grades'>(
        '/frontend_api/makeGroup',
        {
          name: name,
          studentIDs: selectedRecipients.filter((r) => !r.group).map((r) => r.id),
          classIDs: selectedRecipients.filter((r) => !!r.group).map((r) => r.groupID),
        }
      )
        .then((response) => {
          const learnMore = (
            <>
              Learn more about sending broadcast messages{' '}
              <a
                target="_blank"
                className="text-link"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/16245019830297-Sending-Alerts-or-Mass-Broadcasts"
              >
                here
              </a>
              .
            </>
          )
          if (response === 'err-grouping-custom') {
            Popups.confirm({ title: <>We cannot group custom recipients. {learnMore}</>, yesText: 'OK' })
          } else if (response == 'err-grouping-schools') {
            Popups.confirm({ title: <>We cannot group schools. {learnMore}</>, yesText: 'OK' })
          } else if (response == 'err-grouping-grades') {
            Popups.confirm({ title: <>We cannot group grades. {learnMore}</>, yesText: 'OK' })
          } else {
            setPreloadedRecipients((pr) => pr && [...pr, response])
            reloadContacts()
            setComposeState((cs) => cs && { ...cs, selectedRecipients: [response] })
          }
        })
        .catch((error) => {
          showAxiosErrorAsToast(error, 'Failed to save group')
        })
        .finally(() => {
          setLoadingGroup(false)
        })
    })
  }, [composeState.selectedRecipients, reloadContacts, setComposeState, setState, user])
}

export const composeStatesDifferent = (cs: ComposeState, ocs: ComposeState) => {
  return (
    cs.subject != ocs.subject ||
    // dont compare bodyHTML because it is sometimes undefined in original compose state (makes it load from delta)
    (ocs.bodyDelta ? deltaToText(ocs.bodyDelta) != deltaToText(cs.bodyDelta) : cs.bodyHTML != ocs.bodyHTML) ||
    [...cs.sendTypes].join() != [...ocs.sendTypes].join() ||
    cs.selectedRecipients.map((r) => (r.group ? r.groupID : r.id)).join() !=
      ocs.selectedRecipients.map((r) => (r.group ? r.groupID : r.id)).join() ||
    cs.recipientsInfo.individuals.map((r) => `${r.studentID}-${r.guardianID}`).join() !=
      ocs.recipientsInfo.individuals.map((r) => `${r.studentID}-${r.guardianID}`).join() ||
    cs.recipientsInfo.extraInvites.map((r) => `${r.studentID}-${r.guardianID}`).join() !=
      ocs.recipientsInfo.extraInvites.map((r) => `${r.studentID}-${r.guardianID}`).join() ||
    cs.massAllowReplying != ocs.massAllowReplying ||
    cs.sharedTeachersSeeReplies != ocs.sharedTeachersSeeReplies ||
    cs.regardingStudent?.id != ocs.regardingStudent?.id ||
    cs.recipientsInfo.sharedTeacherRecipients.map((r) => (r.group ? r.groupID : r.id)).join() !=
      ocs.recipientsInfo.sharedTeacherRecipients.map((r) => (r.group ? r.groupID : r.id)).join() ||
    cs.recipientsInfo.toGuardians != ocs.recipientsInfo.toGuardians ||
    cs.recipientsInfo.toStudents != ocs.recipientsInfo.toStudents ||
    cs.recipientsInfo.toSharedTeachers != ocs.recipientsInfo.toSharedTeachers
  )
}

export const singleSelectedRecipient = (composeState: ComposeState) => {
  if (
    composeState.selectedRecipients.length !== 1 ||
    composeState.recipientsInfo.individuals.length !== 1 ||
    composeState.selectedRecipients[0].group
  )
    return undefined

  const recipient = composeState.selectedRecipients[0] as SingleStudentRecipient
  const individual = composeState.recipientsInfo.individuals[0]
  return individual.guardianID === undefined
    ? recipient
    : recipient.guardians.find((g) => g.id === individual.guardianID)
}

export const draftToComposeState = async (user: User, draft: MessageData) => {
  const data = draft.draftRequest!
  let neededPeople = Array.isArray(data.recipients) ? [data.recipients[0].student_id] : (data.recipients.people ?? [])
  let neededClasses = Array.isArray(data.recipients) ? [] : (data.recipients.courses ?? [])
  let neededTeachers = data.sharedTeachers ?? []

  const sendTypes = new Set<SendType>(data.type == 'both' ? ['email', 'text'] : [data.type as SendType])
  if (data.type == 'mass') {
    if (data.massSendTypes.phone) sendTypes.add('phone')
    if (data.massSendTypes.email) sendTypes.add('email')
    if (data.massSendTypes.text) sendTypes.add('text')
  }

  const cs: ComposeState = {
    ...DEFAULT_COMPOSE_STATE,
    draftID: draft.id,
    sendTypes,
    massAllowReplying: data.allow_replies,
    regardingStudent: (user.type === 'guardian' ? user.children : []).find(
      (c) => 'regardingStudent' in data.recipients && c.id == data.recipients.regardingStudent
    ),
    sharedTeachersSeeReplies: data.allowRepliesToShared,
    recipientsInfo: {
      ...DEFAULT_RECIPIENTS_INFO,
      toGuardians: 'toParents' in data.recipients && data.recipients.toParents,
      toStudents: 'toStudents' in data.recipients && data.recipients.toStudents,
      toSharedTeachers: neededTeachers.length > 0,
    },
    subject: data.subject || '',
    bodyDelta: stringToDelta(data.body),
    bodyHTML: undefined, // makes it load from delta
    attachments: (data.attachments || []).map((f, i) => ({ fileName: f, uuid: i.toString() })),
    recording:
      data.recording && data.recordingURL ? { serverID: data.recording, audioUrl: data.recordingURL } : undefined,
    initiallySelectedRecipients: {
      users: neededPeople,
      groups: neededClasses,
      individuals: Array.isArray(data.recipients)
        ? data.recipients
            .filter((r) => !('teacherOnly' in r) || !r.teacherOnly)
            .map((r) => ('parent_id' in r && r.parent_id ? r.parent_id : r.student_id))
        : [],
      shared: neededTeachers,
    },
    emergency: data.emergency,
  }
  return cs
}

export const getComposeIndividualsFromIDs = (user: User, recipients: Recipient[], individuals?: number[]) => {
  const composeIndividuals: StudentGuardianRecipient[] = []
  if (user.type === 'teacher' && recipients.length === 1 && !recipients[0].group && individuals) {
    const student = recipients[0] as SingleStudentRecipient
    if (individuals.length === 0) composeIndividuals.push({ studentID: student.id, guardianID: undefined })
    for (const individualID of individuals.filter(
      (i) => student.id === i || student.guardians.find((gg) => gg.id === i)
    ))
      composeIndividuals.push({
        studentID: student.id,
        guardianID: student.id === individualID ? undefined : individualID,
      })
  }
  return composeIndividuals
}
